import { useCallback, useEffect, useRef, useState } from "react";
import type RTKClient from "@cloudflare/realtimekit";
import { HOST_AUDIO_CAPTURE_CONSTRAINTS } from "./realtimeKitPresets";

function stopStreamTracks({ stream }: { stream: MediaStream | null }) {
  stream?.getTracks().forEach((track) => track.stop());
}

async function acquireHostMediaStream({
  videoDeviceId,
  audioDeviceId,
}: {
  videoDeviceId: string;
  audioDeviceId: string;
}): Promise<MediaStream | null> {
  try {
    return await navigator.mediaDevices.getUserMedia({
      video: {
        width: { ideal: 1280 },
        height: { ideal: 720 },
        ...(videoDeviceId ? { deviceId: { exact: videoDeviceId } } : true),
      },
      audio: {
        ...HOST_AUDIO_CAPTURE_CONSTRAINTS,
        ...(audioDeviceId ? { deviceId: { exact: audioDeviceId } } : {}),
      },
    });
  } catch {
    return null;
  }
}

export function useHostMediaStream({
  meeting,
  videoDeviceId,
  audioDeviceId,
  previewVideoEnabled,
}: {
  meeting: RTKClient | undefined;
  videoDeviceId: string;
  audioDeviceId: string;
  previewVideoEnabled: boolean;
}) {
  const videoElementRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [videoTrack, setVideoTrack] = useState<MediaStreamTrack | null>(null);
  const [audioTrack, setAudioTrack] = useState<MediaStreamTrack | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!meeting) {
      setReady(false);
      setVideoTrack(null);
      setAudioTrack(null);
      return;
    }

    let cancelled = false;

    void (async () => {
      stopStreamTracks({ stream: streamRef.current });
      streamRef.current = null;

      const stream = await acquireHostMediaStream({ videoDeviceId, audioDeviceId });
      if (cancelled) {
        stopStreamTracks({ stream });
        return;
      }

      streamRef.current = stream;
      setVideoTrack(stream?.getVideoTracks()[0] ?? null);
      setAudioTrack(stream?.getAudioTracks()[0] ?? null);
      setReady(Boolean(stream));
    })();

    return () => {
      cancelled = true;
    };
  }, [meeting, videoDeviceId, audioDeviceId]);

  const bindPreviewVideo = useCallback(
    (el: HTMLVideoElement | null) => {
      videoElementRef.current = el;
      if (!el) return;

      if (!previewVideoEnabled || !videoTrack) {
        el.srcObject = null;
        return;
      }

      el.srcObject = new MediaStream([videoTrack]);
      void el.play().catch(() => {});
    },
    [previewVideoEnabled, videoTrack],
  );

  useEffect(() => {
    bindPreviewVideo(videoElementRef.current);
  }, [bindPreviewVideo]);

  useEffect(() => {
    return () => {
      stopStreamTracks({ stream: streamRef.current });
      streamRef.current = null;
    };
  }, []);

  return { videoRef: bindPreviewVideo, videoTrack, audioTrack, ready };
}
